// Card #3311 -- "never show a half-painted image", at the board level.
//
// The server picks the active slide from the wall clock, so a poll can name a
// new slide whose picture this browser has not finished downloading and
// decoding. Rendering it then would show an empty (or half-painted) slide. This
// hook keeps the PREVIOUS slide of the same Image Loop on screen until the new
// slide's picture is ready in slideImageCache, then lets the new one through.
// The hold is bounded without a timer: the server clock keeps moving, so the
// next poll names a later slide, and a load that fails or hangs is recorded as
// failed by the cache, which releases the hold (ImageLayout then falls back to
// Blank + logo, scoreboard doctrine: never an error).
//
// It also starts the downloads: the active slide's picture and the NEXT slide's
// (`slideshow.nextImageAssetUrl`), so the next one is ready before its turn.
//
// Only the slide itself is held (`mode`, `slideshow`); the theme, banner and
// everything else stay live. Nothing is held on the first screen, a different
// loop, or anything that is not an Image Loop slide.
import { useEffect, useRef } from "react";
import { IMAGE_STATUS, getSlideImageEntry, imageKeyOf, preloadSlideImage, useSlideImageVersion } from "./slideImageCache";

export default function useHeldSlideshowData(data) {
  useSlideImageVersion();
  const shownRef = useRef(null); // the data whose slide is on screen
  const slideshow = data?.slideshow ?? null;
  const url = slideshow ? data?.mode?.imageAssetUrl : null;
  const nextUrl = slideshow?.nextImageAssetUrl ?? null;

  useEffect(() => {
    if (url) preloadSlideImage(url);
    if (nextUrl) preloadSlideImage(nextUrl);
  }, [url, nextUrl]);

  if (!slideshow || !imageKeyOf(url)) {
    shownRef.current = null;
    return data;
  }
  const shown = shownRef.current;
  const entry = getSlideImageEntry(url);
  const pending = !entry || entry.status === IMAGE_STATUS.LOADING;
  if (shown && shown.slideshow?.id === slideshow.id && pending) {
    return { ...data, mode: shown.mode, slideshow: shown.slideshow };
  }
  shownRef.current = data;
  return data;
}
